
import { APP_BASE_HREF } from '@angular/common';
import { Inject, Injectable, OnDestroy } from '@angular/core';

import { ToastMessageOptions, MessageService } from 'primeng/api';
import { BehaviorSubject, Observable, Subscription, forkJoin, lastValueFrom, of, throwError } from 'rxjs';
import { catchError, filter, map, mergeMap, switchMap, tap } from 'rxjs/operators';

import { ResourceActionTypes } from '../../../Common/Basic/enums/resource-actions.enum';
import { JournodexGeographyModel } from '../../../Common/Journodex/models/journodex-geography.model';
import { JournodexRegionsService } from '../../../Common/Journodex/services/journodex-regions.service';
import { Mention } from '../../../Common/Mentions/models/mention';
import { attributeTypesEnum } from '../../../Common/Misc/enums/attribute-types.enum';
import { messageSeverityEnum } from '../../../Common/Misc/enums/message-severity.enum';
import { AttributeModel } from '../../../Common/Misc/models/attribute.model';
import { TagModel } from '../../../Common/Tags/models/tag.model';
import { LocalizationService } from '../../../Core/Localization/services/localization.service';
import { GeographyTypesEnum } from '../../../api/journodex/enum/geography-types.enum';
import { MentionsApiErrorEnum } from '../../../api/mentions/enum/mentions-api-error.enum';
import { MentionPatchModel, MentionsApi, TVEyesArchivePatchModel } from '../../../api/mentions/mentions.api';
import { MentionIdMercuryMapModel } from '../../../api/mentions/model/mention-id-mercury-map.model';
import { MentionModel } from '../../../api/mentions/model/mention.model';
import { MentionsBatchResponseApiModel } from '../../../api/mentions/model/mentions-batch-response-api.model';
import { mentionAttributeTypesEnum } from '../../Shared/enums/mention-attribute-types.enum';
import { MonitoringBulkActionSourceMethodEnum } from '../../Shared/enums/monitoring-bulk-action-source-method.enum';
import { BulkMentionActionModel } from '../../Shared/models/bulk-mention-action.model';
import { BulkMentionRequestModel } from '../../Shared/models/bulk-mention-request-model';
import { MentionSearchQueryModel } from '../../Shared/models/mention-search-query.model';
import { MentionSearchModel } from '../../Shared/models/mention-search.model';
import { SaveMentionsModel } from '../../Shared/models/save-mentions.model';
import { CurrentUserStateService } from '../../../Core/Users/services/current-user-state.service';
import { AbstractMentionsService } from '../../../Common/Dialogs/services/abstract-mentions.service';


@Injectable()
export class MentionsService extends AbstractMentionsService implements OnDestroy {

    private mentions: Mention[];
    private mentionsProvider: BehaviorSubject<Mention[]>;

    protected focusedMention: Mention;
    public focusedMentionProvider: BehaviorSubject<Mention>;

    private mentionsCount: number;
    private mentionsCountProvider: BehaviorSubject<number>;
    protected userTimezonePreference: string;

    private subscriptions: Subscription[] = [];


    constructor(
        protected messageService: MessageService,
        protected mentionsApi: MentionsApi,
        protected localizationService: LocalizationService,
        protected journodexRegionsService: JournodexRegionsService,
        protected currentUserStateService: CurrentUserStateService,
        @Inject (APP_BASE_HREF) public appBaseHref: string
    ) {
        super();
        this.createProviders();
        this.subscriptions.push(
            this.currentUserStateService.getPreferences().pipe(filter(x => !!x)).subscribe(
                preferences => {
                    if (preferences) {
                        this.userTimezonePreference = preferences.timeZone;
                    }
                }
            ),
        );
    }


    /**
     * Gets the current list of mentions as an observable
     *
     * @returns Observable<Mention[]>
    */
    public getMentions(): Observable<Mention[]> {
        return this.mentionsProvider;
    }

    /**
     * Sets the mention to the provided value
    */
    public setMentions(mentions: Mention[]) {
        this.mentions = mentions;
        this.mentionsProvider.next(mentions);
    }

    /**
     * Sets the list to an error state
    */
    public setMentionsError(error: Error) {
        this.mentionsProvider.error(error);
    }

    /**
     * Gets the current focused mention as an observable
    */
    public getFocusedMention(): Observable<Mention> {
        return this.focusedMentionProvider;
    }

    /**
     * Sets the focused mention to a new value
    */
    public setFocusedMention(mention: Mention) {
        this.focusedMention = mention;
        this.focusedMentionProvider.next(mention);
    }

    /**
     * Gets the current number of mentions as an observable (this can't be done with the mention[] as those results are paginated)
     *
     * @returns Observable<number>
    */
    public getMentionsCount(): Observable<number> {
        return this.mentionsCountProvider;
    }

    /**
     * Sets the focused mention to a new value
    */
    public setMentionsCount(count: number) {
        this.mentionsCount = count;
        this.mentionsCountProvider.next(count);
    }

    /**
     * Refetches any mentions that have been changed by id.
     * Updates the current set of mentions and the focused mention if they have changed
     * isUpdate should be true whenever the body of the mention may have changed, like editing the mention
     */
    public refetchMentions(ids: string[], isUpdate: boolean = true, isMercury: boolean = false): Observable<MentionsBatchResponseApiModel> {
        let request;
        if (!isMercury) {
            request = this.mentionsApi.getMentionsBatched(ids);
        } else {
            request = this.mentionsApi.getMercuryMentionsBatched(ids).pipe(
                map<MentionModel[], MentionsBatchResponseApiModel>(
                    response => {
                        const responseObject = {};
                        response.forEach(mention => {
                            responseObject[mention.mercuryId] = mention;
                        });
                        return responseObject;
                    }
                )
            );
        }
        request.subscribe((response: MentionsBatchResponseApiModel) => {
            if (!this.mentions) {
                // Some other action has made this request no longer important, don't bother processing
                return;
            }
            for (let i = 0, l = this.mentions.length; i < l; i++) {
                const idField = isMercury ? 'id' : 'mentionId';
                const newMention = response[this.mentions[i][idField]];
                if (newMention) {
                    const oldMention = this.mentions[i];
                    this.mentions[i] = new Mention(newMention);

                    // retain contextual information based on the original source of the data
                    this.mentions[i].duplicateGroupId = oldMention.duplicateGroupId;

                    // re-add the syndicate info after a re-get (values don't change)
                    if (oldMention.syndicateInfo) {
                        this.mentions[i].syndicateInfo = oldMention.syndicateInfo;
                    }

                    if (oldMention.mentionId && isUpdate) {
                        // The mention was saved previously and is now updated, because the body could have changed, we can't use the old body
                        continue;
                    }

                    /**
                     * When a mention gets saved, we want to still show a contextual summary, the backend will provide this on "summary" sometimes (where
                     * copyright rules allow for it), in cases that it the back end doesn't have a summary we should use the
                     * body from oldMention so that the context remains the same
                     */
                    if (
                        (oldMention.mentionId && !isUpdate) ||
                        (this.mentions[i].summary === null || this.mentions[i].summary === undefined)
                    ) {
                        this.mentions[i].body = oldMention.body;
                        this.mentions[i].contexualKeywordIndices = oldMention.contexualKeywordIndices.slice();
                    } else {
                        this.mentions[i].useSummaryForDisplay = true;
                    }
                }
            }
            this.setMentions(this.mentions);
            if (this.focusedMention && this.focusedMention.mentionId in response) {
                this.setFocusedMention(
                    this.mentions.find(
                        (mention) => {
                            return mention.mentionId === this.focusedMention.mentionId;
                        }
                    )
                );
            }
        });

        return request;
    }

    /**
     * Saves the provided mention if it has not been saved,
     * and applies updates to the saved mention once it has been saved
    */
    public upsertMention(mention: Mention, search?: MentionSearchModel): Observable<null> {
        const saveMentionsModel = new SaveMentionsModel([mention], search);

        const isSaved: boolean = !!mention.mentionId;
        let request;

        if (isSaved) {
            request = this.updateSavedMention(mention);
            request.subscribe({
                next: () => this.onUpsertMentionSuccess(true),
                error: (error) => this.onUpsertMentionError(error)
            });
        } else {
            const mentions: Mention [] = this.mentions.filter(mentionItem => mentionItem.id === mention.id);

            request = this.mentionsApi.saveMentions(saveMentionsModel.toApiModel());
            request.pipe(
                mergeMap((mentionIDMap: MentionIdMercuryMapModel[]) => {
                    mention.mentionId = mentionIDMap.find(mapping => mapping.mercuryId === mention.id).mentionId;
                    return this.updateMentionsFromSaveResult(mentions, mentionIDMap);
                }),
                mergeMap(() => {
                    return this.updateSavedMention(mention);
                }),
                catchError(error => {
                    this.onUpsertMentionError(error);
                    return throwError(error);
                }))
                .subscribe(
                    savedMentions => this.onUpsertMentionSuccess(),
                );
        }

        return request;
    }

    /**
     * Makes updates to the provided recently saved mentions
     * using the provided mention ID maps
     * which are created when saving mentions
    */
    private updateMentionsFromSaveResult(
        mentions: Mention[],
        mentionIDMaps: MentionIdMercuryMapModel[]
    ): Observable<Mention[]> {
        const observables: Observable<Mention>[] = mentionIDMaps.map(
            (mentionIDMap, index) => this.updateMentionFromSaveResult(mentions[index], mentionIDMap)
        );

        const observable: Observable<Mention[]> = forkJoin(observables);

        return observable;
    }

    /**
     * Updates the provided saved mention
    */
    public updateSavedMention(mention: Mention): Observable<Mention> {
        const apiEntity: MentionModel = mention.toUpdateMentionApiModel();
        const observable: Observable<Mention> = this.mentionsApi
            .updateMention(apiEntity)
            .pipe(
                mergeMap(() => this.refetchMentions([mention.mentionId])),
                mergeMap((batchResponse) => of(new Mention(batchResponse[mention.mentionId])))
            );

        return observable;
    }

    /**
     * patch a specific mention and return the mention
    */
    public patchAndReturnMention(diff: MentionPatchModel): Observable<Mention> {
        const observable: Observable<Mention> = this.mentionsApi
            .patchMention(diff)
            .pipe(
                mergeMap(() => this.refetchMentions([diff.id])),
                mergeMap((batchResponse) => of(new Mention(batchResponse[diff.id])))
            );

        return observable;
    }

    /**
     * Updates the provided recently saved mention using
     * using the provided mention ID map
     * which is created when saving a mention
    */
    private updateMentionFromSaveResult(mention: Mention, mentionIDMap: MentionIdMercuryMapModel): Observable<Mention> {
        mention.id = mentionIDMap.mercuryId;
        mention.mentionId = mentionIDMap.mentionId;
        return this.updateSavedMention(mention);
    }

    /**
     * Responds after updating a mention
    */
    private onUpsertMentionSuccess(isSaved?: boolean): void {
        let saveMentionToAccountDetail = this.localizationService.transform('MentionDetails.saveMentionToAccount');
        if (isSaved) {
            saveMentionToAccountDetail = this.localizationService.transform('Monitoring.result.mentionsAlreadySavedToAccount', { count: 1 });
        }

        const successMessage: ToastMessageOptions = {
            severity: messageSeverityEnum.success,
            summary: this.localizationService.transform('MentionDetails.saveMentionsHeading'),
            detail: saveMentionToAccountDetail
        };

        this.messageService.add(successMessage);
    }

    /**
     * Responds to errors encountered
     * while updating a mention
    */
    private onUpsertMentionError(error: any): void {
        let errorMessage;
        if (error?.error && error.error.includes('Available quota')) {
            errorMessage = {
                severity: messageSeverityEnum.error,
                summary: 'MentionFoldersService.saveMentions.reachQuota.heading',
                detail: 'MentionFoldersService.saveMentions.reachQuota.detail'
            };
        } else {
            errorMessage = {
                severity: messageSeverityEnum.error,
                summary: this.localizationService.transform('MentionDetails.saveMentionFailedHeading'),
                detail: error
            };
        }

        this.messageService.add(errorMessage);
    }

    /**
     * Creates the providers this services relies on
    */
    private createProviders(): void {
        this.mentions = null;
        this.mentionsProvider = new BehaviorSubject<Mention[]>(null);

        this.focusedMention = null;
        this.focusedMentionProvider = new BehaviorSubject<Mention>(null);

        this.mentionsCount = null;
        this.mentionsCountProvider = new BehaviorSubject<number>(null);
    }

    patchTVEyesMention(diff: TVEyesArchivePatchModel): Observable<null> {
        const id = diff.id;

        if (!id) {
            return of(null);
        }

        return this.mentionsApi.patchTVEyesMention(diff).pipe(
            mergeMap(() => this.refetchMentions([id])),
            tap({
                next: (result) => {
                    const mention = result[id];
                    this.messageService.add({
                        severity: messageSeverityEnum.success,
                        summary: 'Mention.editDialog.edit.success.summary',
                        detail: this.localizationService.transform('Mention.editDialog.edit.success.detail', {
                            name: mention.title
                        })
                    });
                },
                error: () => {
                    this.messageService.add({
                        severity: messageSeverityEnum.error,
                        summary: 'Mention.editDialog.edit.error.summary',
                        detail: 'Mention.editDialog.edit.error.detail'
                    });
                }
            }),
            map(() => null)
        );
    }


    /**
     * Patches specific properties of a mention
     */
    patchMention(diff: MentionPatchModel): Observable<null> {
        const id = diff?.id;

        if (!id) {
            return of(null);
        }

        const request = this.mentionsApi.patchMention(diff);

        request.pipe(
            mergeMap(() => {
                return this.refetchMentions([id]);
            }))
            .subscribe({
                next: (result) => {
                    const mention = result[id];
                    this.messageService.add({
                        severity: messageSeverityEnum.success,
                        summary: this.localizationService.transform('Mention.editDialog.edit.success.summary'),
                        detail: this.localizationService.transform('Mention.editDialog.edit.success.detail', {name: mention.title})
                    });
                },
                error: () => {
                    this.messageService.add({
                        severity: messageSeverityEnum.error,
                        summary: this.localizationService.transform('Mention.editDialog.edit.error.summary'),
                        detail: this.localizationService.transform('Mention.editDialog.edit.error.detail')
                    });
                }
            });

        return request;
    }

    /**
     * Creates a custom mention
     */
    createCustomMention(mention: Mention): Observable<string> {
        const request = this.mentionsApi.createCustomMention(mention.getCreateModel());

        request.subscribe({
            next: () => {
                this.messageService.add({
                    severity: messageSeverityEnum.success,
                    summary: this.localizationService.transform('Mention.editDialog.create.success.summary'),
                    detail: this.localizationService.transform('Mention.editDialog.create.success.detail', {name: mention.headline})
                });
            },
            error: (error) => {
                if (error
                    && error.error
                    && error.error.details
                    && error.error.details[0]?.code === MentionsApiErrorEnum.folderLimitReached
                ) {
                    this.messageService.add({
                        severity: messageSeverityEnum.error,
                        summary: this.localizationService.transform('MentionFoldersService.saveMentionsFailedHeading'),
                        detail: this.localizationService.transform('MentionFoldersService.saveMentionsFailedDetail.limitReached')
                    });
                } else {
                    this.messageService.add({
                        severity: messageSeverityEnum.error,
                        summary: this.localizationService.transform('Mention.editDialog.create.error.summary'),
                        detail: this.localizationService.transform('Mention.editDialog.create.error.detail', {name: mention.headline})
                    });
                }

                throw error;
            }
        });

        return request;
    }

    saveTagsToMentions(
        mentionIds: string[],
        tags: TagModel[]
    ): Observable<void> {
        if (tags.length === 0 || mentionIds.length === 0) {
            return;
        }

        const bulkAction = new BulkMentionActionModel();
        bulkAction.addTags = tags.map(tag => tag.id);
        const bulkReq = new BulkMentionRequestModel(
            MonitoringBulkActionSourceMethodEnum.SELECTION,
            bulkAction,
            null,
            mentionIds
        );
        const request = this.mentionsApi.updateMentionsInBulk(bulkReq.toApi());

        return request.pipe(
            tap(() => {
                this.refetchMentions(mentionIds, false);
                let detail = '';
                if (tags.length === 1) {
                    detail = this.localizationService.transform(
                        'Monitoring.result.saveMentionsToTag',
                        {
                            'count': mentionIds.length,
                            'tagName': tags[0].name
                        }
                    );
                } else {
                    detail = this.localizationService.transform(
                        'Monitoring.result.saveMentionsToMultipleTags',
                        {
                            'count': mentionIds.length,
                            'numTags': tags.length
                        }
                    );
                }

                this.messageService.add({
                    severity: messageSeverityEnum.success,
                    summary: this.localizationService.transform('Monitoring.result.saveMentionsHeading'),
                    detail: detail
                });
            })
        );
    }

    /**
     * Saves a set of mentions to the account
     */
    saveMentionsToAccount(
        mentions: Mention[],
        refetchMentions: boolean = true,
        search?: MentionSearchModel,
        tags?: TagModel[],
        forceSyndicateSave?: boolean
    ): Observable<MentionIdMercuryMapModel[]> {

        const saveMentions = new SaveMentionsModel(mentions, search);

        const numberOfAlreadySaved = saveMentions.getAlreadySavedMentions().length;
        const numberOfNewSaves = saveMentions.mentions.length - numberOfAlreadySaved;

        return this.mentionsApi.saveMentions(saveMentions.toApiModel(forceSyndicateSave)).pipe(
            tap(() => {
                const messages = [];
                if (numberOfNewSaves > 0) {
                    messages.push({
                        severity: messageSeverityEnum.success,
                        summary: this.localizationService.transform('Monitoring.result.saveMentionsHeading'),
                        detail: this.localizationService.transform(
                            'Monitoring.result.saveMentionsToAccount',
                            { 'count': numberOfNewSaves })
                    });
                }
                if (numberOfAlreadySaved > 0) {
                    messages.push({
                        severity: messageSeverityEnum.success,
                        summary: this.localizationService.transform('Monitoring.result.saveMentionsHeading'),
                        detail: this.localizationService.transform(
                            'Monitoring.result.mentionsAlreadySavedToAccount',
                            { 'count': numberOfAlreadySaved })
                    });
                }
                this.messageService.addAll(messages);
            }),
            map((data) => {
                // updates the saved mention ids so we can batch refresh them
                const updateMentionIds = [];
                const allMentionIds = [];
                for (const idMapping of data) {
                    const mercuryId = idMapping.mercuryId;
                    const mentionId = idMapping.mentionId;
                    const mappedMention = saveMentions.mentions.find((mention) => {
                        if (mention.id) {
                            return mention.id === mercuryId;
                        }
                        if (mention.mentionId) {
                            // mention that have already been saved have mentionId
                            return mention.mentionId === mentionId;
                        }
                    });
                    if (mappedMention) {
                        mappedMention.mentionId = mentionId; // For New cases mentions that have not been saved
                        updateMentionIds.push(mappedMention.mentionId);
                    }
                    allMentionIds.push(mentionId);
                }

                if (refetchMentions) {
                    this.refetchMentions(updateMentionIds, false);
                }

                return {allMentionIds, data};
            }),
            switchMap(({allMentionIds, data}) => {
                if (tags?.length > 0) {
                    return this.saveTagsToMentions(allMentionIds, tags).pipe(
                        catchError(err => of(null)),
                        map(() => data) // Always returns mentionIds
                    );
                }
                return of(data);
            }),
            catchError((error) => {
                if (error?.error?.includes('Available quota')) {
                    this.messageService.add({
                        severity: messageSeverityEnum.error,
                        summary: 'MentionFoldersService.saveMentions.reachQuota.heading',
                        detail: 'MentionFoldersService.saveMentions.reachQuota.detail'
                    });
                } else {
                    this.messageService.add({
                        severity: messageSeverityEnum.error,
                        summary: this.localizationService.transform('Monitoring.result.saveMentionsFailedHeading'),
                        detail: this.localizationService.transform('Monitoring.result.saveMentionsFailedDetails')
                    });
                }
                return throwError(() => error);
            })
        );
    }


    /**
     * Removes mentions from the user account, from each folder and from all saved mentions
     *
     * @param mentions the list of mentions to be removed
     */
    public removeMentionsFromAccount(mentions: Mention[], query: MentionSearchQueryModel, queryMentionsCount: number,  reason?: string): Observable<null> {
        const mercuryMentions = mentions.map((mention) =>  mention.convertToApiMention());
        let mentionsCount: number = mentions.length;
        if (query) {
            mentionsCount = queryMentionsCount;
        }

        if (query?.dateQuery && !query.dateQuery.timezone) {
            query.dateQuery.timezone = this.userTimezonePreference;
        }

        return this.mentionsApi.deleteMentions(mercuryMentions, query?.getApiEntity(), reason).pipe(
            tap(_ => {
                this.messageService.add({
                    severity: messageSeverityEnum.success,
                    summary: this.localizationService.transform('Mention.removeFromAccount.success.heading'),
                    detail: this.localizationService.transform('Mention.removeFromAccount.success.message', { 'count': mentionsCount })
                });
            }),
            catchError(error => {
                const errorMessage: ToastMessageOptions = {
                    severity: messageSeverityEnum.error,
                    summary: this.localizationService.transform('Mention.removeFromAccount.error.heading')
                };
                if (error.status === 404) {
                    errorMessage.detail = this.localizationService.transform(
                        'Mention.removeFromAccount.error.message.404'
                    );

                    this.messageService.add(errorMessage);
                } else {
                    // @TODO: Handle other errors?
                    errorMessage.detail = this.localizationService.transform(
                        'Mention.removeFromAccount.error.message'
                    );

                    this.messageService.add(errorMessage);
                }
                return throwError(error);
            })
        );
    }

    /**
     * Gets the journodex region mapping for a mention object.
     */
    getMentionRegion(mention: Mention): Observable<JournodexGeographyModel> {
        let type: GeographyTypesEnum;
        let id: number;

        if (mention.cityId) {
            id = mention.cityId;
            type = GeographyTypesEnum.city;
        } else if (mention.countyId) {
            id = mention.countyId;
            type = GeographyTypesEnum.county;
        } else if (mention.provinceId) {
            id = mention.provinceId;
            type = GeographyTypesEnum.province;
        } else if (mention.countryId) {
            id = mention.countryId;
            type = GeographyTypesEnum.country;
        } else if (mention.continentId) {
            id = mention.continentId;
            type = GeographyTypesEnum.continent;
        } else {
            return of(null);
        }

        return this.journodexRegionsService.getRegions([id], type).pipe(
            map((regions) => {
                return regions[0];
            }));
    }

    async getCountOfArchivedMentionsFromMentionIds(mentionIds: string[]): Promise<number> {
        const mentionsResponseModel = await lastValueFrom(this.mentionsApi.getMentionsBatched(mentionIds));

        const responseIds = Object.keys(mentionsResponseModel);
        const archivedBroadcastMentions = responseIds.filter(mentionId => {
            const mention = mentionsResponseModel[mentionId];
            if (mention && mention.isArchivedCriticalMention) {
                return true;
            }

            return false;
        });

        if (!archivedBroadcastMentions) {
            return 0;
        }

        return archivedBroadcastMentions.length;
    }

    getMentionInformation(mention: Mention): AttributeModel[] {
        if (!mention) {
            return [];
        }

        const mediaTypeAttribute: AttributeModel = {
            id: mentionAttributeTypesEnum.mediaType,
            mediaType: mention.mediaType.id,
            label: 'Mention.details.mediaType',
            value: mention.mediaType.name,
            type: attributeTypesEnum.simple,
            icon: mention.mediaType.icon
        };

        const sentimentAttribute: AttributeModel = {
            id: mentionAttributeTypesEnum.sentiment,
            label: 'Mention.details.sentiment',
            value: mention.sentimentName,
            type: attributeTypesEnum.sentiment,
            infoTooltip: 'Monitoring.sentiment.tooltip'
        };

        const audienceReachAttribute: AttributeModel = {
            id: mentionAttributeTypesEnum.audienceReach,
            label: 'Mention.details.audienceReach',
            value: mention.audienceReach,
            type: attributeTypesEnum.audienceReach,
            isSimilarWeb: mention.isAudienceFromSimilarWeb,
            icon: 'fa-solid fa-arrows-to-eye'
        };

        const adValueUsdAttribute: AttributeModel = {
            id: mentionAttributeTypesEnum.adValueUsd,
            label: 'Mention.details.ave',
            value: mention.adValueUsd,
            type: attributeTypesEnum.adValueUsd,
            icon: 'fa-solid fa-circle-dollar',
            infoTooltip: 'Mention.details.aveTooltip'
        };

        const domainAuthorityAttribute: AttributeModel = {
            id: mentionAttributeTypesEnum.domainAuthority,
            label: 'Mention.details.domainAuthority',
            value: mention.domainAuthority,
            type: attributeTypesEnum.domainAuthority,
            icon: 'fa-solid fa-gauge-simple-high',
            infoTooltip: 'Mention.details.domainAuthorityTooltip'
        };

        const wordCountAttribute: AttributeModel = {
            id: mentionAttributeTypesEnum.wordCount,
            label: 'Mention.details.wordCount',
            value: mention.wordCount,
            type: attributeTypesEnum.wordCount,
            icon: 'fa-solid fa-memo'
        };

        const durationAttribute: AttributeModel = {
            id: mentionAttributeTypesEnum.duration,
            label: 'Mention.details.duration',
            value: mention.duration,
            type: attributeTypesEnum.duration,
            icon: 'fa-solid fa-stopwatch',
            infoTooltip: 'Mention.details.durationTooltip'
        };

        const languageAttribute: AttributeModel = {
            id: mentionAttributeTypesEnum.language,
            label: 'Mention.details.language',
            value: mention.language,
            type: attributeTypesEnum.language,
            icon: 'fa-kit fa-agpr-language'
        };

        const mentionInformation = [
            mediaTypeAttribute,
            sentimentAttribute,
            audienceReachAttribute,
            adValueUsdAttribute,
            domainAuthorityAttribute,
            wordCountAttribute,
            durationAttribute,
            languageAttribute
        ];

        if (mention?.licenseSummary) {
            const licenseSummary = mention.licenseSummary;
            const mercuryId = licenseSummary?.mercuryId ?? '';
            const mercuryIdInfo = (licenseSummary?.licenseName && mercuryId) ? ` - ${mercuryId}` : mercuryId;
            const displayValue = this.localizationService.transform(licenseSummary.licenseName) + licenseSummary.additionalInfo + mercuryIdInfo;
            if (displayValue) {
                mentionInformation.push({
                    id: mentionAttributeTypesEnum.license,
                    label: 'Mention.details.license',
                    value: displayValue,
                    type: attributeTypesEnum.license,
                    icon: 'fa-solid fa-memo-circle-info',
                    showCopyIcon: !!mercuryId,
                    copyValue: mercuryId,
                    copyToolTip: 'Common.id',
                    isMultiline: true,
                });
            }

        }

        if (mention.isTvEyes()) {
            const url = mention.getTVEyesDisplayUrl();

            if (url) {
                const urlAttribute: AttributeModel = {
                    id: mentionAttributeTypesEnum.url,
                    label: 'Mention.details.url',
                    value: url,
                    type: attributeTypesEnum.url,
                    url: url,
                    icon: 'fa-solid fa-link',
                    showCopyIcon: true,
                    copyValue: url,
                    copyToolTip: 'Mention.details.url',
                };
                mentionInformation.push(urlAttribute);
            }
        }

        return mentionInformation;
    }

    updateMentionInsights(patch: MentionPatchModel, name: string, actionType: ResourceActionTypes): Observable<Mention> {
        return this.patchAndReturnMention(patch).pipe(
            tap(x => {
                let messageSummary: string;
                let messageDetail: string;
                switch (actionType) {
                    case ResourceActionTypes.UPDATE:
                        messageSummary = 'Mention.insights.update.summary';
                        messageDetail = 'Mention.insights.update.detail';
                        break;
                    case ResourceActionTypes.SAVE:
                        messageSummary = 'Mention.insights.save.summary';
                        messageDetail = 'Mention.insights.save.detail';
                        break;
                    case ResourceActionTypes.DELETE:
                        messageSummary = 'Mention.insights.delete.summary';
                        messageDetail = 'Mention.insights.delete.detail';
                        break;
                }

                this.messageService.add({
                    severity: messageSeverityEnum.success,
                    summary: this.localizationService.transform(messageSummary),
                    detail: this.localizationService.transform(messageDetail, {insightName: name})
                });
            }),
            catchError(
                x => {
                    this.messageService.add({
                        severity: messageSeverityEnum.error,
                        summary: this.localizationService.transform('Mention.insights.error.genericSummary'),
                        detail: this.localizationService.transform('Mention.insights.error.genericDetail')
                    });

                    return of(null);
                }
            ),
        );
    }

    revertTvEyesToOriginal(mentionId: string): Observable<null> {
        return this.mentionsApi.revertTVEyesMention(mentionId).pipe(
            catchError((error) => {
                this.messageService.add({
                    severity: messageSeverityEnum.error,
                    summary: 'Mention.details.tvEyesRevert.error.summary',
                    detail: 'Mention.details.tvEyesRevert.error.details'
                });

                throw error;
            }),
            tap(() => {
                this.messageService.add({
                    severity: messageSeverityEnum.success,
                    summary: 'Mention.details.tvEyesRevert.success.summary',
                    detail: 'Mention.details.tvEyesRevert.success.details'
                });
            }),
            switchMap(() => this.refetchMentions([mentionId])),
            map(() => null)
        );
    }

    getMentionById(id: string): Observable<Mention> {
        return this.mentionsApi.getMention(id).pipe(map(apiModel => new Mention(apiModel)));
    }

    getMentionByIds(ids: string[]): Observable<MentionsBatchResponseApiModel> {
        return this.mentionsApi.getMentionsBatched(ids);
    }

    getMentionByMercuryIds(ids: string[], isAddTvEyesInstaPlayUrls?: boolean): Observable<MentionModel[]> {
        return this.mentionsApi.getMercuryMentionsBatched(ids, isAddTvEyesInstaPlayUrls);
    }

    ngOnDestroy() {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }
}
